@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.login

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.coroutines.await
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import org.example.project.dashboard.DashboardTheme
import org.example.project.datenschutz.DatenschutzTexte
import org.example.project.datenschutz.SignaturDatenschutzOverlay
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.manager.NodeScope
import org.example.project.service.LoginUserApi
import org.example.project.service.LoginService
import org.example.project.service.PasswordChangeApi
import org.example.project.service.PasswordPolicy
import org.example.project.service.PasswordResetApi
import org.example.project.settings.LogoSettings
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.PasswordRequirementHint
import org.example.project.registrierung.NetzwerkTeilnehmerUebersicht
import org.example.project.utils.All
import org.example.project.utils.DashboardVerlaufHintergrund
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.events.Event
import org.w3c.fetch.RequestInit

private const val CUS_DESKTOP_CANVAS = "cus-desktop-signatur-canvas"

private val loginJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private suspend fun saveLoginSignatur(userId: String, signatur: String): Boolean {
    if (userId.isBlank() || signatur.isBlank()) return false
    return runCatching {
        window.fetch(
        "${LumaticaApiSettings.getBaseUrl()}/api/desktoploginscreen/signatur-save-json",
        RequestInit(
            method = "POST",
            headers = LumaticaApiManager.auditHeaders("application/json"),
            body = loginJson.encodeToString(
                buildJsonObject {
                    put("userId", JsonPrimitive(userId))
                    put("signatur", JsonPrimitive(signatur))
                }
            )
        )
        ).await().ok
    }.getOrDefault(false)
}

@Composable
fun DesktopLoginScreen(art: String, onSubmit: (String, String) -> Unit) {
    Style(LoginStyles)
    Style(UnifiedAccessStyles)
    DashboardVerlaufHintergrund()

    val scope = rememberCoroutineScope()
    val loginRequestId = remember { newLoginRequestId() }
    val unifiedBrandAccess = art == NodeScope.SYS || art == NodeScope.CUS

    var step     by remember { mutableStateOf("LOGIN") }
    var kennung by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var error    by remember { mutableStateOf(false) }
    var errorText by remember { mutableStateOf("") }
    var loading  by remember { mutableStateOf(false) }
    var resetLoading by remember { mutableStateOf(false) }
    var resetMessage by remember { mutableStateOf<String?>(null) }

    var pendingUser        by remember { mutableStateOf<All?>(null) }
    var pendingNewPassword by remember { mutableStateOf("") }
    var zweiFaktorCode by remember { mutableStateOf("") }
    var zweiFaktorToken by remember { mutableStateOf("") }
    var zweiFaktorEinrichtung by remember { mutableStateOf(false) }
    var zweiFaktorQrCode by remember { mutableStateOf("") }
    var trustedDevice by remember { mutableStateOf(false) }

    var newPassword    by remember { mutableStateOf("") }
    var repeatPassword by remember { mutableStateOf("") }
    var passwordError  by remember { mutableStateOf(false) }

    var canvasGezeichnet      by remember { mutableStateOf(false) }
    var datenschutzAkzeptiert by remember { mutableStateOf(false) }
    var signaturFehler        by remember { mutableStateOf(false) }
    var isMobile by remember { mutableStateOf(window.innerWidth <= 768) }

    DisposableEffect(Unit) {
        val resizeListener: (Event) -> Unit = { isMobile = window.innerWidth <= 768 }
        window.addEventListener("resize", resizeListener)
        onDispose { window.removeEventListener("resize", resizeListener) }
    }
    fun completeLoginStep(user: All) {
        error = false
        pendingUser = user
        errorText = ""
        when {
            !user.passwortVerifiziert -> step = "PASSWORT"
            user.signatur.isBlank() || !user.datenschutzAkzeptiert -> step = "SIGNATUR"
            else -> { LoginService.login(user); onSubmit(kennung, password) }
        }
    }

    fun submitLogin() {
        if (loading) return
        if (step == "LOGIN" && kennung.isBlank()) {
            error = true
            errorText = if (art == NodeScope.SYS) "Bitte E-Mail eingeben." else "Bitte Personalnummer oder E-Mail eingeben."
            return
        }
        if (step == "LOGIN" && password.isBlank()) {
            error = true
            errorText = "Bitte Passwort eingeben."
            return
        }
        loading = true
        scope.launch {
            try {
                when (val result = UnifiedLoginFlow.submit(
                    LoginFlowRequest(
                        kennung = kennung,
                        password = password,
                        target = LoginTarget.DESKTOP,
                        loginRequestId = loginRequestId,
                        zweiFaktorToken = if (step == "ZWEI_FAKTOR") zweiFaktorToken else "",
                        zweiFaktorCode = zweiFaktorCode,
                        trustedDevice = trustedDevice && !zweiFaktorEinrichtung
                    )
                )) {
                    is LoginFlowResult.Authenticated -> {
                        LoginService.captureSession(result.user)
                        completeLoginStep(result.user)
                    }
                    is LoginFlowResult.ZweiFaktor -> {
                        zweiFaktorToken = result.token
                        zweiFaktorEinrichtung = result.setup
                        zweiFaktorQrCode = result.qrCode
                        zweiFaktorCode = ""
                        trustedDevice = false
                        error = false
                        errorText = ""
                        step = "ZWEI_FAKTOR"
                    }
                    is LoginFlowResult.Rejected -> {
                        error = true
                        errorText = result.message
                    }
                }
            } finally {
                loading = false
            }
        }
    }

    fun requestPasswordReset() {
        val email = kennung.trim().lowercase()
        if (email.isBlank()) {
            error = true
            errorText = "Bitte E-Mail eingeben."
            resetMessage = null
            return
        }
        if (loading || resetLoading) return
        resetLoading = true
        error = false
        errorText = ""
        resetMessage = null
        scope.launch {
            try {
                if (PasswordResetApi.request(email, "desktop")) {
                    resetMessage = "Wenn ein Konto existiert, wurde eine E-Mail versendet."
                } else {
                    error = true
                    errorText = "E-Mail konnte nicht versendet werden. Bitte später erneut versuchen."
                }
            } finally {
                resetLoading = false
            }
        }
    }

    fun openRegistration() {
        window.location.href = if (window.location.hostname == "localhost") "http://localhost:2000/register" else "https://lumatica.de/register"
    }

    fun doChangePassword() {
        if (newPassword != repeatPassword || PasswordPolicy.error(newPassword) != null) { passwordError = true; return }
        passwordError = false
        pendingNewPassword = newPassword
        step = "SIGNATUR"
    }

    fun doCompleteOnboarding() {
        if (!canvasGezeichnet) { signaturFehler = true; return }
        signaturFehler = false
        scope.launch {
            val canvas = document.getElementById(CUS_DESKTOP_CANVAS) as? HTMLCanvasElement
            if (canvas == null) { signaturFehler = true; return@launch }
            var user = (pendingUser ?: return@launch)
            if (pendingNewPassword.isNotBlank()) {
                user = PasswordChangeApi.changePassword(user.id, pendingNewPassword) ?: run {
                    signaturFehler = true
                    return@launch
                }
                pendingUser = user
            }
            val signatur = canvas.toDataURL("image/png")
            if (!saveLoginSignatur(user.id, signatur)) {
                signaturFehler = true
                return@launch
            }
            val savedUser = LoginUserApi.saveUser(user.copy(datenschutzAkzeptiert = true, signatur = ""))
                ?: user.copy(datenschutzAkzeptiert = true, signatur = "")
            org.example.project.service.GlobalManager.triggerSessionConfigReload()
            LoginService.login(savedUser.copy(signatur = signatur, datenschutzAkzeptiert = true))
            onSubmit(kennung, pendingNewPassword.ifBlank { password })
        }
    }

    // Signatur-Schritt (Vollbild-Overlay)
    if (step == "SIGNATUR") {
        Div({ classes(LoginStyles.backgroundStripes) })
        SignaturDatenschutzOverlay(
            canvasId = CUS_DESKTOP_CANVAS,
            datenschutzText = DatenschutzTexte.SIGNATUR,
            datenschutzLabel = "Ich habe die Datenschutzhinweise gelesen und akzeptiere diese.",
            buttonLabel = "Profil abschließen und anmelden",
            canvasHoehePx = 160,
            maxWidthPx = 560,
            overlayStyle = {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                backgroundColor(DashboardTheme.BACKGROUND_APP)
                property("background", DashboardTheme.BACKGROUND_APP_GRADIENT)
                property("z-index", "10")
                property("font-family", "system-ui, Segoe UI, Arial")
            },
            cardStyle = {
                backgroundColor(DashboardTheme.SURFACE_DEEP)
                property("border", "1px solid #2a2d34")
                borderRadius(20.px)
                padding(40.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(20.px)
                property("box-sizing", "border-box")
                property("box-shadow", "0 12px 48px rgba(0,0,0,0.8)")
            },
            canvasGezeichnet = canvasGezeichnet,
            onCanvasGezeichnetChange = { canvasGezeichnet = it },
            datenschutzAkzeptiert = datenschutzAkzeptiert,
            onDatenschutzAkzeptiertChange = { datenschutzAkzeptiert = it },
            fehlerText = if (signaturFehler) {
                if (!canvasGezeichnet) "Bitte zeichne deine Unterschrift." else "Signatur konnte nicht gespeichert werden."
            } else null,
            onComplete = { doCompleteOnboarding() }
        )
        return
    }

    if (step == "ZWEI_FAKTOR") {
        UnifiedLoginLayout(isMobile) {
            ZweiFaktorLoginUi(
                setup = zweiFaktorEinrichtung,
                qrCode = zweiFaktorQrCode,
                code = zweiFaktorCode,
                trustedDevice = trustedDevice,
                loading = loading,
                error = error,
                errorText = errorText,
                onCodeChange = {
                    zweiFaktorCode = it
                    error = false
                },
                onTrustedDeviceChange = { trustedDevice = it },
                onSubmit = ::submitLogin
            )
            UnifiedAccessButton(
                label = if (loading) "..." else "Code prüfen",
                enabled = !loading,
                onClick = ::submitLogin
            )
        }
        return
    }

    if (unifiedBrandAccess && step == "LOGIN") {
        UnifiedLoginLayout(isMobile) {
            UnifiedLoginContent(
                identifier = kennung,
                password = password,
                identifierPlaceholder = if (art == NodeScope.SYS) "E-Mail" else "Personalnummer oder E-Mail",
                loading = loading,
                errorText = if (error) errorText else null,
                infoText = resetMessage,
                forgotLabel = if (art == NodeScope.SYS) (if (resetLoading) "..." else "Passwort vergessen") else null,
                onIdentifierChange = { kennung = it; error = false; resetMessage = null },
                onPasswordChange = { password = it; error = false },
                onSubmit = ::submitLogin,
                onForgot = if (art == NodeScope.SYS) ::requestPasswordReset else null
            )
        }
        return
    }

    // Login + Passwort-Schritt
    Div({ classes(LoginStyles.backgroundStripes) })
    Div({
        classes(LoginStyles.stage)
        if (unifiedBrandAccess) classes(LoginStyles.sysStage, UnifiedAccessStyles.stage)
        if (isMobile) {
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                padding(16.px)
                overflow("auto")
            }
        }
    }) {
        if (unifiedBrandAccess && !isMobile) {
            Div({ classes(UnifiedAccessStyles.mapPane) }) {
                NetzwerkTeilnehmerUebersicht(compact = true)
            }
            Div({ classes(UnifiedAccessStyles.divider) })
        }
        if (!isMobile && !unifiedBrandAccess) {
            Div({
                classes(LoginStyles.leftPane)
            }) {
                Div({ style { position(Position.Relative); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center) } }) {
                    Img(src = LogoSettings.desktopLoginHeroLogo, attrs = {
                        style {
                            width(LogoSettings.desktopLoginHeroWidthPx.px)
                            height(LogoSettings.desktopLoginHeroHeightPx.px)
                            property("object-fit", "contain")
                            property("opacity", "0")
                            property("animation", "${LoginStyles.fadeIn.name} 600ms ease 120ms forwards")
                            marginBottom((-10).px)
                        }
                    })
                    H1({ classes(LoginStyles.brand) }) { Text("") }
                }
            }
            Div({ classes(LoginStyles.divider) })
        }
        Div({
            classes(LoginStyles.rightPane)
            if (unifiedBrandAccess) classes(UnifiedAccessStyles.panel, UnifiedAccessStyles.mobilePanel)
            if (isMobile && !unifiedBrandAccess) {
                style {
                    padding(24.px)
                    maxWidth(500.px)
                    width(100.percent)
                    property("box-sizing", "border-box")
                }
            }
        }) {
            if (unifiedBrandAccess) {
                Img(src = LogoSettings.desktopRegistrationHeaderLogo, attrs = {
                    classes(UnifiedAccessStyles.logo)
                })
            }
            when (step) {
                "LOGIN" -> {
                    if (unifiedBrandAccess) {
                        UnifiedLoginContent(
                            identifier = kennung,
                            password = password,
                            identifierPlaceholder = if (art == NodeScope.SYS) "E-Mail" else "Personalnummer oder E-Mail",
                            loading = loading,
                            errorText = if (error) errorText else null,
                            forgotLabel = "Passwort vergessen",
                            switchPrefix = "Neu hier?",
                            switchLabel = "Registrieren",
                            onIdentifierChange = { kennung = it; error = false },
                            onPasswordChange = { password = it; error = false },
                            onSubmit = ::submitLogin,
                            onForgot = ::requestPasswordReset,
                            onSwitch = ::openRegistration
                        )
                    } else {
                    Input(type = InputType.Text, attrs = {
                        id("u"); classes(LoginStyles.input, LoginStyles.inputDelay1)
                        if (unifiedBrandAccess) classes(UnifiedAccessStyles.input)
                        value(kennung)
                        attr("autocomplete", "username")
                        placeholder(if (art == NodeScope.SYS) "E-Mail" else "Personalnummer oder E-Mail")
                        onInput { kennung = it.value }
                        onKeyDown {
                            if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                                it.preventDefault()
                                submitLogin()
                            }
                        }
                    })
                    Input(type = InputType.Password, attrs = {
                        id("p"); classes(LoginStyles.input, LoginStyles.inputDelay2)
                        if (unifiedBrandAccess) classes(UnifiedAccessStyles.input)
                        value(password); attr("autocomplete", "current-password"); placeholder("Passwort")
                        onInput { password = it.value }
                        onKeyDown {
                            if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                                it.preventDefault()
                                submitLogin()
                            }
                        }
                    })
                    if (error) Div({ classes(LoginStyles.error) }) { Text(errorText) }
                    }
                }
                "PASSWORT" -> {
                    Div({ classes(LoginStyles.pwLabel) }) { Text("Passwort setzen") }
                    Div({ classes(LoginStyles.pwSubtitle) }) { Text("Bitte lege ein persönliches Passwort fest.") }
                    Input(type = InputType.Password, attrs = {
                        classes(LoginStyles.input); if (unifiedBrandAccess) classes(UnifiedAccessStyles.input); placeholder("Neues Passwort"); value(newPassword)
                        onInput { newPassword = it.value; passwordError = false }
                    })
                    PasswordRequirementHint(newPassword)
                    Input(type = InputType.Password, attrs = {
                        classes(LoginStyles.input); if (unifiedBrandAccess) classes(UnifiedAccessStyles.input); placeholder("Wiederholen"); value(repeatPassword)
                        onInput { repeatPassword = it.value; passwordError = false }
                        onKeyDown {
                            if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                                it.preventDefault()
                                doChangePassword()
                            }
                        }
                    })
                    if (passwordError) Div({ classes(LoginStyles.error) }) { Text(PasswordPolicy.error(newPassword) ?: "Passwörter stimmen nicht überein.") }
                }
                "ZWEI_FAKTOR" -> {
                    ZweiFaktorLoginUi(
                        setup = zweiFaktorEinrichtung,
                        qrCode = zweiFaktorQrCode,
                        code = zweiFaktorCode,
                        trustedDevice = trustedDevice,
                        loading = loading,
                        error = error,
                        errorText = errorText,
                        onCodeChange = {
                            zweiFaktorCode = it
                            error = false
                        },
                        onTrustedDeviceChange = { trustedDevice = it },
                        onSubmit = { submitLogin() }
                    )
                }
                "SIGNATUR" -> {}
            }
            if (!(unifiedBrandAccess && step == "LOGIN")) Div({ style { marginTop(4.px) } }) {
                val actionLabel = when {
                        loading -> "..."
                        step == "LOGIN" -> "Anmelden"
                        step == "PASSWORT" -> "Weiter"
                        step == "ZWEI_FAKTOR" -> "Code prüfen"
                        else -> ""
                    }
                val action = {
                        when (step) {
                            "LOGIN" -> if (!loading) submitLogin()
                            "PASSWORT" -> doChangePassword()
                            "ZWEI_FAKTOR" -> if (!loading) submitLogin()
                            else -> {}
                        }
                    }
                if (unifiedBrandAccess) UnifiedAccessButton(actionLabel, !loading, onClick = action)
                else LumaticaButton(
                    label = actionLabel, onClick = action, variant = LumaticaButtonVariant.Action,
                    enabled = !loading, fillWidth = true, heightPx = 44, driverLoginReference = true
                )
            }
        }
    }
}

/* Styles */

object LoginStyles : StyleSheet() {
    private val cBg          = DashboardTheme.SURFACE_DEEP
    private val cText        = Color("#f5f6f8")
    private val cBorderHover = Color("#3a3e46")
    private val cErrorBg     = Color("#fff3cd")
    private val cError       = Color("#664d03")
    private val cErrorBorder = Color("#ffecb5")

    val fadeIn by keyframes { from { property("opacity", "0") }; to { property("opacity", "1") } }
    val slideInLeft by keyframes { from { property("opacity", "0"); property("transform", "translateX(-24px)") }; to { property("opacity", "1"); property("transform", "translateX(0)") } }
    val fadeInUp by keyframes { from { property("opacity", "0"); property("transform", "translateY(10px)") }; to { property("opacity", "1"); property("transform", "translateY(0)") } }
    val stripesMove by keyframes { from { property("background-position", "0 0") }; to { property("background-position", "200% 0") } }

    val backgroundStripes by style {
        position(Position.Fixed); top(0.px); left(0.px); width(100.percent); height(100.vh)
        property("background", DashboardTheme.DASHBOARD_BG)
        property("background-size", "150% 100%"); property("animation", "${stripesMove.name} 40s linear infinite")
        property("opacity", "0.18"); property("pointer-events", "none"); property("z-index", "0")
    }
    val sysStage by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        padding(24.px)
        backgroundColor(Color.transparent)
        property("font-family", "Geist, system-ui, Segoe UI, Arial")
    }
    val sysAmbient by style {
        position(Position.Absolute)
        borderRadius(9999.px)
        property("pointer-events", "none")
        property("filter", "blur(90px)")
        opacity(0.32)
    }
    val sysAmbientSilver by style {
        width(38.vw); height(38.vw); left((-12).vw); top((-18).vw)
        property("background", "rgba(190,198,210,0.16)")
    }
    val sysAmbientRed by style {
        width(30.vw); height(30.vw); right((-12).vw); bottom((-16).vw)
        property("background", "rgba(180,0,0,0.13)")
    }
    val sysHero by style {
        gridColumn("1")
        alignItems(AlignItems.FlexEnd)
        paddingRight(72.px)
        property("z-index", "2")
        desc(self, selector("img")) style {
            property("filter", "drop-shadow(0 28px 80px rgba(0,0,0,0.72))")
        }
    }
    val sysLoginPane by style {
        padding(36.px)
        maxWidth(410.px); width(100.percent); gap(16.px)
        borderRadius(18.px)
        property("background", "rgba(5,6,8,0.72)")
        property("border", "1px solid rgba(255,255,255,0.15)")
        property("box-shadow", "0 28px 90px rgba(0,0,0,0.62), inset 0 1px 0 rgba(255,255,255,0.04)")
        property("backdrop-filter", "blur(22px)")
        boxSizing("border-box"); property("z-index", "3")
        property("animation", "${fadeInUp.name} 620ms cubic-bezier(.2,.7,.2,1) 100ms both")
    }
    val sysInput by style {
        height(50.px)
        fontSize(15.px)
        borderRadius(8.px)
        media(mediaMaxWidth(768.px)) {
            self style { height(44.px) }
        }
    }
    val sysMobileLogo by style {
        width(220.px); height(64.px); property("object-fit", "contain"); property("align-self", "center"); marginBottom(18.px)
    }
    val stage by style {
        position(Position.Relative); property("z-index", "1"); backgroundColor(Color.transparent); color(cText)
        height(100.vh); width(100.vw); property("box-sizing", "border-box"); overflow("hidden")
        display(DisplayStyle.Grid); gridTemplateColumns("1fr 1px 1fr"); gridTemplateRows("1fr")
        alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); padding(24.px)
        property("font-family", "system-ui, Segoe UI, Arial")
    }
    val leftPane by style { gridColumn("1"); gridRow("1"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); justifyContent(JustifyContent.Center); alignItems(AlignItems.FlexEnd); paddingRight(64.px); gap(6.px) }
    val brand by style {
        fontSize(24.px); margin(0.px); textAlign("center")
        backgroundImage("linear-gradient(90deg, #5b9bff, #c8deff)")
        property("background-clip", "text"); property("-webkit-background-clip", "text")
        color(Color.transparent); property("-webkit-text-fill-color", "transparent")
        property("animation-name", slideInLeft.name); property("animation-duration", "400ms"); property("animation-delay", "80ms"); property("animation-fill-mode", "forwards")
    }
    val divider by style {
        gridColumn("2"); gridRow("1")
        width(3.px); height(550.px)
        property("border-radius", "999px")
        property(
            "background",
            "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.28) 18%, rgba(255,255,255,0.95) 50%, rgba(255,255,255,0.28) 82%, rgba(255,255,255,0.06) 100%)"
        )
        property("box-shadow", "0 0 24px rgba(255,255,255,0.55), 0 0 10px rgba(255,255,255,0.35)")
        property("opacity", "0")
        property("animation-name", fadeIn.name)
        property("animation-duration", "500ms")
        property("animation-delay", "420ms")
        property("animation-fill-mode", "forwards")
    }
    val rightPane by style { paddingTop(18.px); gridColumn("3"); gridRow("1"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); justifyContent(JustifyContent.Center); paddingLeft(64.px); maxWidth(280.px); width(100.percent); gap(16.px) }
    val input by style {
        width(100.percent); height(44.px); padding(0.px, 14.px); fontSize(15.px); textAlign("center"); borderRadius(8.px)
        property("box-sizing", "border-box")
        backgroundColor(Color.transparent); color(Color.white); property("background", "transparent"); property("border", "1px solid rgba(255,255,255,0.18)"); property("box-shadow", "0 0 0 1px rgba(255,255,255,0.05), 0 0 18px rgba(255,255,255,0.08)"); property("transition", "border-color 120ms ease")
        focus { outline("none"); property("border-color", cBorderHover); property("box-shadow", "none") }
    }
    val inputDelay1 by style { property("animation-name", fadeInUp.name); property("animation-duration", "380ms"); property("animation-delay", "520ms"); property("animation-fill-mode", "forwards") }
    val inputDelay2 by style { property("animation-name", fadeInUp.name); property("animation-duration", "380ms"); property("animation-delay", "680ms"); property("animation-fill-mode", "forwards") }
    val error by style { backgroundColor(cErrorBg); color(cError); property("border", "1px solid $cErrorBorder"); borderRadius(8.px); padding(10.px); fontSize(13.px) }
    /** Passwort-setzen Kopfzeile */
    val pwLabel by style {
        color(cText); fontSize(15.px); fontWeight("bold"); marginBottom((-6).px)
    }
    val pwSubtitle by style {
        color(Color("#8a8f9a")); fontSize(12.px); property("line-height", "1.4")
    }
}







